import React from 'react';
import Select from '../../common/Select';
import Input from '../../common/Input';
import Button from '../../common/Button';

export const RULE_EXACT_SCORE = 'EXACT_SCORE';
export const RULE_SCORE_DIFFERENCE = 'SCORE_DIFFERENCE';
export const RULE_CORRECT_WINNER = 'CORRECT_WINNER';
export const DRAW_ALLOWED = 'DRAW_ALLOWED';
export const DRAW_FORBIDDEN = 'DRAW_FORBIDDEN';
export const DRAW_WITH_TIE_BREAK_WINNER = 'DRAW_WITH_TIE_BREAK_WINNER';
export const BEST_MATCH = 'BEST_MATCH';

const DEFAULT_EXACT_SCORE_POINTS = 3;
const DEFAULT_SCORE_DIFFERENCE_POINTS = 2;
const DEFAULT_CORRECT_WINNER_POINTS = 1;
const DEFAULT_POINTS = 1;
const MIN_POINTS = 0;
const EMPTY_RULE_ERROR = 'Включите хотя бы одно правило с очками больше 0';
const NEGATIVE_POINTS_ERROR = 'Очки не могут быть отрицательными';
const DUPLICATE_RULE_ERROR = 'Одно правило нельзя добавить дважды';
const UNSUPPORTED_RULE_ERROR = 'Можно использовать только правила основного исхода';
const CHANGE_WARNING = 'Изменение профиля влияет только на будущие пари. Уже созданные пари сохраняют свой снапшот правил.';

const RULE_OPTIONS = [
  {
    value: RULE_EXACT_SCORE,
    label: 'Точный счёт',
    hint: 'Прогноз полностью совпал с официальным счётом',
    defaultPoints: DEFAULT_EXACT_SCORE_POINTS,
  },
  {
    value: RULE_SCORE_DIFFERENCE,
    label: 'Точная разница',
    hint: 'Разница счёта совпала с официальной',
    defaultPoints: DEFAULT_SCORE_DIFFERENCE_POINTS,
  },
  {
    value: RULE_CORRECT_WINNER,
    label: 'Победитель',
    hint: 'Правильно выбран победитель или ничья',
    defaultPoints: DEFAULT_CORRECT_WINNER_POINTS,
  },
];

const DRAW_POLICY_OPTIONS = [
  { value: DRAW_ALLOWED, label: 'Ничья разрешена', hint: 'Можно прогнозировать исход X' },
  { value: DRAW_FORBIDDEN, label: 'Ничья запрещена', hint: 'Нужен победитель без ничьей' },
  {
    value: DRAW_WITH_TIE_BREAK_WINNER,
    label: 'Ничья с тай-брейком',
    hint: 'При ничьей нужно выбрать победителя тай-брейка',
  },
];

export const DEFAULT_SCORING_RULES = RULE_OPTIONS.map((rule) => ({
  ruleType: rule.value,
  points: rule.defaultPoints,
}));

export const DEFAULT_SCORING_PROFILE = {
  drawPolicy: DRAW_ALLOWED,
  aggregationStrategy: BEST_MATCH,
  rules: DEFAULT_SCORING_RULES,
};

export const createDefaultScoringRule = (ruleType) => {
  const option = RULE_OPTIONS.find((rule) => rule.value === ruleType);
  return {
    ruleType,
    points: option?.defaultPoints ?? DEFAULT_POINTS,
  };
};

export const normalizeScoringRules = (rules) => {
  const rawRules = toRuleList(rules);
  if (rawRules.length === 0) {
    return cloneDefaultRules();
  }

  const uniqueRules = [];
  const usedTypes = new Set();
  rawRules
    .filter((rule) => rule && rule.ruleType)
    .forEach((rule) => {
      if (usedTypes.has(rule.ruleType)) return;
      usedTypes.add(rule.ruleType);
      uniqueRules.push({
        ruleType: rule.ruleType,
        points: Number(rule.points ?? DEFAULT_POINTS),
      });
    });

  return uniqueRules.length > 0 ? uniqueRules : cloneDefaultRules();
};

export const normalizeScoringProfile = (profile) => ({
  drawPolicy: profile?.drawPolicy || DRAW_ALLOWED,
  aggregationStrategy: profile?.aggregationStrategy || BEST_MATCH,
  rules: normalizeScoringRules(profile?.rules),
});

export const validateScoringRules = (rules) => {
  const rulesToValidate = toRuleList(rules);
  const safeRules = rulesToValidate.length > 0 ? rulesToValidate : cloneDefaultRules();
  const usedTypes = new Set();
  let hasPositiveRule = false;

  for (const rule of safeRules) {
    if (!rule?.ruleType || !RULE_OPTIONS.some((option) => option.value === rule.ruleType)) {
      return UNSUPPORTED_RULE_ERROR;
    }
    if (usedTypes.has(rule.ruleType)) {
      return DUPLICATE_RULE_ERROR;
    }
    usedTypes.add(rule.ruleType);

    if (!Number.isInteger(Number(rule.points)) || Number(rule.points) < MIN_POINTS) {
      return NEGATIVE_POINTS_ERROR;
    }
    if (Number(rule.points) > MIN_POINTS) {
      hasPositiveRule = true;
    }
  }

  return hasPositiveRule ? '' : EMPTY_RULE_ERROR;
};

export const toScoringProfilePayload = (profile) => {
  const normalizedProfile = normalizeScoringProfile(profile);
  return {
    drawPolicy: normalizedProfile.drawPolicy,
    aggregationStrategy: normalizedProfile.aggregationStrategy,
    rules: toScoringRulesMap(normalizedProfile.rules),
  };
};

export const toScoringRulesMap = (rules) => normalizeScoringRules(rules).reduce((accumulator, rule) => ({
  ...accumulator,
  [rule.ruleType]: Number(rule.points),
}), {});

export const formatDrawPolicyLabel = (drawPolicy) => (
  DRAW_POLICY_OPTIONS.find((option) => option.value === drawPolicy)?.label || 'Ничья разрешена'
);

export const describeScoringRules = (rules) => normalizeScoringRules(rules)
  .map((rule) => `${getRuleLabel(rule.ruleType)}: ${rule.points}`)
  .join(' · ');

export default function ScoringProfileBuilder({
  value,
  onChange,
  disabled = false,
  title = 'Scoring-профиль',
  description = 'Выберите правила основного исхода и очки за каждое правило.',
  showChangeWarning = false,
  error = '',
}) {
  const profile = normalizeScoringProfile(value);
  const usedTypes = new Set(profile.rules.map((rule) => rule.ruleType));

  const updateProfile = (patch) => {
    onChange({ ...profile, ...patch });
  };

  const updateRule = (index, points) => {
    const nextRules = profile.rules.map((rule, ruleIndex) => (
      ruleIndex === index ? { ...rule, points: Number(points) } : rule
    ));
    updateProfile({ rules: nextRules });
  };

  const addRule = (ruleType) => {
    if (!ruleType || usedTypes.has(ruleType)) {
      return;
    }
    updateProfile({ rules: [...profile.rules, createDefaultScoringRule(ruleType)] });
  };

  const removeRule = (index) => {
    if (profile.rules.length === 1) {
      return;
    }
    updateProfile({ rules: profile.rules.filter((_, ruleIndex) => ruleIndex !== index) });
  };

  return (
    <div className="bm-scoring-builder">
      <div className="bm-scoring-builder__header">
        <div>
          <div className="bm-label">{title}</div>
          <p className="bm-text-secondary bm-text-sm">{description}</p>
        </div>
      </div>

      {showChangeWarning && (
        <div className="bm-scoring-builder__warning">
          {CHANGE_WARNING}
        </div>
      )}

      <Select
        label="Политика ничьей"
        value={profile.drawPolicy}
        onChange={(drawPolicy) => updateProfile({ drawPolicy })}
        options={DRAW_POLICY_OPTIONS}
        disabled={disabled}
      />

      <div className="bm-scoring-builder__rules">
        {profile.rules.map((rule, index) => (
          <div className="bm-scoring-builder__rule" key={rule.ruleType}>
            <div className="bm-scoring-builder__rule-copy">
              <strong>{getRuleLabel(rule.ruleType)}</strong>
              <span>{getRuleHint(rule.ruleType)}</span>
            </div>
            <Input
              label="Очки"
              type="number"
              min={MIN_POINTS}
              value={String(rule.points)}
              onChange={(event) => updateRule(index, event.target.value)}
              disabled={disabled}
            />
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => removeRule(index)}
              disabled={disabled || profile.rules.length === 1}
              style={{ color: 'var(--bm-accent-red)' }}
            >
              Удалить
            </Button>
          </div>
        ))}
      </div>

      <div className="bm-scoring-builder__actions">
        {RULE_OPTIONS.filter((option) => !usedTypes.has(option.value)).map((option) => (
          <Button
            key={option.value}
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => addRule(option.value)}
            disabled={disabled}
          >
            Добавить: {option.label}
          </Button>
        ))}
      </div>

      {error && <span className="bm-input-error-msg">{error}</span>}

      <p className="bm-text-secondary bm-text-sm">
Снапшот будущего пари:
      </p>
    </div>
  );
}

// ===================================================================================================================
// = Implementation
// ===================================================================================================================

function cloneDefaultRules() {
  return DEFAULT_SCORING_RULES.map((rule) => ({ ...rule }));
}

function toRuleList(rules) {
  if (Array.isArray(rules)) {
    return rules;
  }
  if (rules && typeof rules === 'object') {
    return Object.entries(rules).map(([ruleType, points]) => ({ ruleType, points }));
  }
  return [];
}

function getRuleLabel(ruleType) {
  return RULE_OPTIONS.find((option) => option.value === ruleType)?.label || ruleType;
}

function getRuleHint(ruleType) {
  return RULE_OPTIONS.find((option) => option.value === ruleType)?.hint || '';
}
